import { useEffect, useState, useRef, useCallback } from "react";

const MouseGlow = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [clicks, setClicks] = useState([]);
  const [particles, setParticles] = useState([]);
  const [trail, setTrail] = useState([]);
  const [hovers, setHovers] = useState([]);
  const [isMoving, setIsMoving] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverIntensity, setHoverIntensity] = useState(0);
  const [velocity, setVelocity] = useState({ x: 0, y: 0 });
  const [colorPhase, setColorPhase] = useState(0);
  
  const lastPosition = useRef({ x: 0, y: 0 });
  const moveTimeout = useRef(null);
  const hoverTimeout = useRef(null);
  const animationFrame = useRef(null);
  const idCounter = useRef(0);
  const lastHoverTime = useRef(0);

  // Generate unique ID
  const generateId = useCallback(() => {
    idCounter.current += 1;
    return `${Date.now()}-${idCounter.current}`;
  }, []);

  // Advanced color cycling based on movement and time
  const getGlowColors = useCallback(() => {
    const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
    const intensity = Math.min(speed / 10, 1);
    
    const colors = [
      { primary: 'rgb(147, 51, 234)', secondary: 'rgb(79, 70, 229)', accent: 'rgb(236, 72, 153)' }, // Purple-Indigo-Pink
      { primary: 'rgb(59, 130, 246)', secondary: 'rgb(16, 185, 129)', accent: 'rgb(34, 197, 94)' }, // Blue-Teal-Green
      { primary: 'rgb(245, 101, 101)', secondary: 'rgb(251, 146, 60)', accent: 'rgb(252, 211, 77)' }, // Red-Orange-Yellow
      { primary: 'rgb(168, 85, 247)', secondary: 'rgb(139, 92, 246)', accent: 'rgb(196, 181, 253)' }, // Purple spectrum
      { primary: 'rgb(6, 182, 212)', secondary: 'rgb(14, 165, 233)', accent: 'rgb(59, 130, 246)' }  // Cyan-Blue spectrum
    ];
    
    const colorIndex = Math.floor((colorPhase + intensity * 2) % colors.length);
    return colors[colorIndex];
  }, [velocity, colorPhase]);

  // Create hover ripple effects
  const createHoverEffect = useCallback((x, y, intensity = 1) => {
    const newHover = {
      id: generateId(),
      x,
      y,
      intensity,
      scale: 0,
      opacity: 0.8,
      life: 1500
    };
    
    setHovers(prev => [...prev, newHover]);
    
    // Remove hover effect after animation
    setTimeout(() => {
      setHovers(prev => prev.filter(h => h.id !== newHover.id));
    }, 1500);
  }, [generateId]);

  // Detect hoverable elements
  const detectHoverableElement = useCallback((x, y) => {
    const elements = document.elementsFromPoint(x, y);
    return elements.some(el => 
      el.tagName === 'BUTTON' ||
      el.tagName === 'A' ||
      el.tagName === 'INPUT' ||
      el.tagName === 'TEXTAREA' ||
      el.classList.contains('hover-glow') ||
      el.style.cursor === 'pointer' ||
      window.getComputedStyle(el).cursor === 'pointer'
    );
  }, []);

  // Create particle explosion on click
  const createParticles = useCallback((x, y) => {
    const particleCount = 12 + Math.random() * 8;
    const newParticles = [];
    
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.5;
      const speed = 2 + Math.random() * 4;
      const size = 4 + Math.random() * 8;
      const life = 1000 + Math.random() * 1500;
      
      newParticles.push({
        id: generateId(),
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size,
        life,
        maxLife: life,
        color: Math.floor(Math.random() * 360)
      });
    }
    
    setParticles(prev => [...prev, ...newParticles]);
  }, [generateId]);

  // Update hover effects animation
  useEffect(() => {
    const updateHovers = () => {
      setHovers(prev => prev
        .map(hover => ({
          ...hover,
          scale: Math.min(hover.scale + 0.05, 1),
          opacity: Math.max(hover.opacity - 0.01, 0),
          life: hover.life - 16
        }))
        .filter(hover => hover.life > 0)
      );
      
      if (hovers.length > 0) {
        requestAnimationFrame(updateHovers);
      }
    };
    
    if (hovers.length > 0) {
      requestAnimationFrame(updateHovers);
    }
  }, [hovers.length]);

  // Update particles animation
  useEffect(() => {
    const updateParticles = () => {
      setParticles(prev => prev
        .map(particle => ({
          ...particle,
          x: particle.x + particle.vx,
          y: particle.y + particle.vy,
          vx: particle.vx * 0.98, // Friction
          vy: particle.vy * 0.98,
          life: particle.life - 16 // Assuming 60fps
        }))
        .filter(particle => particle.life > 0)
      );
      
      animationFrame.current = requestAnimationFrame(updateParticles);
    };
    
    if (particles.length > 0) {
      animationFrame.current = requestAnimationFrame(updateParticles);
    }
    
    return () => {
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, [particles.length]);

  // Color phase cycling
  useEffect(() => {
    const interval = setInterval(() => {
      setColorPhase(prev => (prev + 0.1) % 5);
    }, 100);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const moveHandler = (e) => {
      const newX = e.clientX;
      const newY = e.clientY;
      
      // Calculate velocity
      const vx = newX - lastPosition.current.x;
      const vy = newY - lastPosition.current.y;
      setVelocity({ x: vx, y: vy });
      
      setPosition({ x: newX, y: newY });
      setIsMoving(true);
      
      // Check for hoverable elements
      const isOverHoverable = detectHoverableElement(newX, newY);
      const currentTime = Date.now();
      
      if (isOverHoverable) {
        if (!isHovering) {
          setIsHovering(true);
          setHoverIntensity(0);
        }
        
        // Gradually increase hover intensity
        setHoverIntensity(prev => Math.min(prev + 0.1, 1));
        
        // Create periodic hover ripples
        if (currentTime - lastHoverTime.current > 300) {
          createHoverEffect(newX, newY, 0.6 + Math.random() * 0.4);
          lastHoverTime.current = currentTime;
        }
      } else {
        if (isHovering) {
          setIsHovering(false);
          setHoverIntensity(0);
        }
      }
      
      // Add to trail
      setTrail(prev => [
        { x: newX, y: newY, id: generateId() },
        ...prev.slice(0, 8) // Keep last 8 positions
      ]);
      
      lastPosition.current = { x: newX, y: newY };
      
      // Reset moving state after delay
      clearTimeout(moveTimeout.current);
      moveTimeout.current = setTimeout(() => {
        setIsMoving(false);
        setVelocity({ x: 0, y: 0 });
      }, 150);
    };

    const clickHandler = (e) => {
      const newClick = {
        x: e.clientX,
        y: e.clientY,
        id: generateId(),
        intensity: Math.random() * 0.5 + 0.5
      };
      
      setClicks(prev => [...prev, newClick]);
      createParticles(e.clientX, e.clientY);
      createHoverEffect(e.clientX, e.clientY, 1.2); // Stronger effect on click
      
      // Remove click effect after animation
      setTimeout(() => {
        setClicks(prev => prev.filter(c => c.id !== newClick.id));
      }, 800);
    };

    const keyHandler = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        createParticles(position.x, position.y);
      }
    };

    window.addEventListener("mousemove", moveHandler);
    window.addEventListener("click", clickHandler);
    window.addEventListener("keydown", keyHandler);

    return () => {
      window.removeEventListener("mousemove", moveHandler);
      window.removeEventListener("click", clickHandler);
      window.removeEventListener("keydown", keyHandler);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const cleanupMoveTimeout = moveTimeout.current;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const cleanupHoverTimeout = hoverTimeout.current;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const cleanupAnimationFrame = animationFrame.current;
      if (cleanupMoveTimeout) clearTimeout(cleanupMoveTimeout);
      if (cleanupHoverTimeout) clearTimeout(cleanupHoverTimeout);
      if (cleanupAnimationFrame) cancelAnimationFrame(cleanupAnimationFrame);
    };
  }, [position.x, position.y, createParticles, generateId, detectHoverableElement, createHoverEffect, isHovering]);

  const currentColors = getGlowColors();
  const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
  const glowIntensity = Math.min(speed / 5, 1);
  const totalIntensity = Math.max(glowIntensity, hoverIntensity);

  return (
    <>
      {/* Main following glow with dynamic colors and intensity */}
      <div
        className="pointer-events-none fixed z-0 rounded-full mix-blend-screen"
        style={{
          width: `${300 + totalIntensity * 120}px`,
          height: `${300 + totalIntensity * 120}px`,
          transform: `translate(${position.x - (150 + totalIntensity * 60)}px, ${position.y - (150 + totalIntensity * 60)}px)`,
          background: `radial-gradient(circle at center, ${currentColors.primary}${Math.floor((0.3 + totalIntensity * 0.5) * 255).toString(16).padStart(2, '0')}, ${currentColors.secondary}${Math.floor((0.2 + totalIntensity * 0.4) * 255).toString(16).padStart(2, '0')}, transparent)`,
          filter: `blur(${80 + totalIntensity * 50}px)`,
          transition: isMoving || isHovering ? 'transform 0.05s ease-out' : 'transform 0.3s ease-out, width 0.3s ease-out, height 0.3s ease-out',
          opacity: 0.8 + totalIntensity * 0.2
        }}
      />

      {/* Hover-specific glow layer */}
      {isHovering && (
        <div
          className="pointer-events-none fixed z-0 rounded-full mix-blend-screen animate-pulse"
          style={{
            width: `${200 + hoverIntensity * 100}px`,
            height: `${200 + hoverIntensity * 100}px`,
            transform: `translate(${position.x - (100 + hoverIntensity * 50)}px, ${position.y - (100 + hoverIntensity * 50)}px)`,
            background: `radial-gradient(circle at center, ${currentColors.accent}${Math.floor(hoverIntensity * 0.6 * 255).toString(16).padStart(2, '0')}, transparent)`,
            filter: `blur(${60 + hoverIntensity * 30}px)`,
            transition: 'all 0.2s ease-out',
            animationDuration: `${1.5 - hoverIntensity * 0.5}s`
          }}
        />
      )}

      {/* Secondary glow layer */}
      <div
        className="pointer-events-none fixed z-0 w-64 h-64 rounded-full mix-blend-screen"
        style={{
          transform: `translate(${position.x - 128}px, ${position.y - 128}px)`,
          background: `radial-gradient(circle at center, ${currentColors.accent}40, transparent)`,
          filter: 'blur(60px)',
          transition: 'transform 0.08s ease-out',
          opacity: 0.6
        }}
      />

      {/* Mouse trail */}
      {trail.map((point, index) => (
        <div
          key={point.id}
          className="pointer-events-none fixed z-0 rounded-full mix-blend-screen"
          style={{
            width: `${20 - index * 2}px`,
            height: `${20 - index * 2}px`,
            transform: `translate(${point.x - (10 - index)}px, ${point.y - (10 - index)}px)`,
            background: `radial-gradient(circle, ${currentColors.primary}${Math.floor((0.8 - index * 0.1) * 255).toString(16).padStart(2, '0')}, transparent)`,
            filter: `blur(${10 + index * 2}px)`,
            opacity: 1 - index * 0.12
          }}
        />
      ))}

      {/* Hover ripple effects */}
      {hovers.map((hover) => (
        <div key={hover.id}>
          {/* Main hover ripple */}
          <div
            className="pointer-events-none fixed z-10 rounded-full mix-blend-screen"
            style={{
              width: `${80 * hover.scale * hover.intensity}px`,
              height: `${80 * hover.scale * hover.intensity}px`,
              left: hover.x - (40 * hover.scale * hover.intensity),
              top: hover.y - (40 * hover.scale * hover.intensity),
              background: `radial-gradient(circle, ${currentColors.accent}${Math.floor(hover.opacity * 0.4 * 255).toString(16).padStart(2, '0')}, transparent)`,
              filter: `blur(${20 * hover.scale}px)`,
              opacity: hover.opacity,
              transform: `scale(${hover.scale})`,
              transition: 'transform 0.1s ease-out'
            }}
          />
          
          {/* Hover ring effect */}
          <div
            className="pointer-events-none fixed z-10 rounded-full mix-blend-screen"
            style={{
              width: `${120 * hover.scale}px`,
              height: `${120 * hover.scale}px`,
              left: hover.x - (60 * hover.scale),
              top: hover.y - (60 * hover.scale),
              border: `1px solid ${currentColors.primary}${Math.floor(hover.opacity * 0.3 * 255).toString(16).padStart(2, '0')}`,
              filter: 'blur(1px)',
              opacity: hover.opacity * 0.7,
              transform: `scale(${hover.scale})`
            }}
          />
        </div>
      ))}

      {/* Click burst effects */}
      {clicks.map((click) => (
        <div key={click.id}>
          {/* Main burst */}
          <div
            className="pointer-events-none fixed z-10 rounded-full animate-ping mix-blend-screen"
            style={{
              width: `${120 * click.intensity}px`,
              height: `${120 * click.intensity}px`,
              left: click.x - (60 * click.intensity),
              top: click.y - (60 * click.intensity),
              background: `radial-gradient(circle, ${currentColors.primary}80, ${currentColors.accent}60, transparent)`,
              filter: 'blur(30px)',
              animationDuration: '0.8s'
            }}
          />
          
          {/* Ring effect */}
          <div
            className="pointer-events-none fixed z-10 rounded-full mix-blend-screen"
            style={{
              width: '200px',
              height: '200px',
              left: click.x - 100,
              top: click.y - 100,
              border: `2px solid ${currentColors.secondary}60`,
              filter: 'blur(2px)',
              animation: 'ping 0.8s cubic-bezier(0, 0, 0.2, 1) infinite'
            }}
          />
        </div>
      ))}

      {/* Floating particles */}
      {particles.map((particle) => {
        const lifeRatio = particle.life / particle.maxLife;
        const size = particle.size * lifeRatio;
        const opacity = lifeRatio * 0.8;
        
        return (
          <div
            key={particle.id}
            className="pointer-events-none fixed z-10 rounded-full mix-blend-screen"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              left: particle.x - size / 2,
              top: particle.y - size / 2,
              background: `hsl(${particle.color + colorPhase * 60}, 70%, 60%)`,
              filter: `blur(${2 + (1 - lifeRatio) * 3}px)`,
              opacity,
              transform: `scale(${lifeRatio})`,
              transition: 'transform 0.1s ease-out'
            }}
          />
        );
      })}

      {/* Ambient floating orbs */}
      <div
        key="ambient-orb-1"
        className="pointer-events-none fixed z-0 w-32 h-32 rounded-full mix-blend-screen animate-pulse"
        style={{
          transform: `translate(${position.x - 64 + Math.sin(colorPhase) * 20}px, ${position.y - 64 + Math.cos(colorPhase * 1.3) * 15}px)`,
          background: `radial-gradient(circle, ${currentColors.accent}30, transparent)`,
          filter: 'blur(40px)',
          animationDuration: '3s',
          animationDelay: '0s'
        }}
      />
      
      <div
        key="ambient-orb-2"
        className="pointer-events-none fixed z-0 w-24 h-24 rounded-full mix-blend-screen animate-pulse"
        style={{
          transform: `translate(${position.x - 48 + Math.cos(colorPhase * 1.7) * 25}px, ${position.y - 48 + Math.sin(colorPhase * 0.8) * 20}px)`,
          background: `radial-gradient(circle, ${currentColors.secondary}25, transparent)`,
          filter: 'blur(35px)',
          animationDuration: '4s',
          animationDelay: '1s'
        }}
      />
    </>
  );
};

export default MouseGlow;
